// StandaloneBooking.js - Fristående bokningssida för iframe och testning
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './StandaloneBooking.css';

const API_URL = process.env.REACT_APP_COURSE_API_URL || 'http://localhost:3002';

function StandaloneBooking() {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  // Formulärdata - endast förnamn, efternamn, e-post
  const [participants, setParticipants] = useState([{
    firstName: '',
    lastName: '',
    email: ''
  }]);

  const [discountCode, setDiscountCode] = useState('');
  const [validatedDiscount, setValidatedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState('');

  // Hämta kurser
  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/public/courses`);
      setCourses(response.data.data || response.data);
    } catch (err) {
      setError('Kunde inte hämta kurser. Försök igen senare.');
      console.error('Error fetching courses:', err);
    } finally {
      setLoading(false);
    }
  };

  // Beräkna pris
  const calculatePrice = useCallback(() => {
    if (!selectedCourse) return 0;

    const basePrice = selectedCourse.price;
    const numParticipants = participants.length;
    let totalPrice = basePrice * numParticipants;

    if (validatedDiscount) {
      if (validatedDiscount.discountType === 'percent') {
        totalPrice = totalPrice * (1 - validatedDiscount.discountValue / 100);
      } else {
        totalPrice = totalPrice - (validatedDiscount.discountValue * numParticipants);
      }
    }

    return Math.max(0, totalPrice);
  }, [selectedCourse, participants.length, validatedDiscount]);

  // Validera rabattkod
  const validateDiscount = async () => {
    if (!discountCode.trim() || !selectedCourse) return;

    try {
      setDiscountError('');
      const response = await axios.post(`${API_URL}/api/public/validate-discount`, {
        code: discountCode.trim(),
        courseId: selectedCourse.courseId
      });

      if (response.data.success) {
        setValidatedDiscount(response.data.data);
      } else {
        setDiscountError(response.data.error || 'Ogiltig rabattkod');
        setValidatedDiscount(null);
      }
    } catch (err) {
      setDiscountError(err.response?.data?.error || 'Kunde inte validera rabattkod');
      setValidatedDiscount(null);
    }
  };

  // Lägg till deltagare
  const addParticipant = () => {
    if (participants.length >= 10) return;
    setParticipants([...participants, {
      firstName: '',
      lastName: '',
      email: ''
    }]);
  };

  // Ta bort deltagare
  const removeParticipant = (index) => {
    if (participants.length <= 1) return;
    setParticipants(participants.filter((_, i) => i !== index));
  };

  // Uppdatera deltagare
  const updateParticipant = (index, field, value) => {
    const updated = [...participants];
    updated[index][field] = value;
    setParticipants(updated);
  };

  // Skicka bokning
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validera
    for (let i = 0; i < participants.length; i++) {
      const p = participants[i];
      if (!p.firstName || !p.lastName || !p.email) {
        setError(`Deltagare ${i + 1}: Förnamn, efternamn och e-post krävs`);
        return;
      }
    }

    try {
      setError(null);
      setLoading(true);

      const bookingData = {
        courseInstanceId: selectedCourse.instanceId,
        participants: participants.map((p, i) => ({
          ...p,
          isPrimary: i === 0
        })),
        discountCode: validatedDiscount ? discountCode : null
      };

      const response = await axios.post(`${API_URL}/api/public/book`, bookingData);
      setBookingResult(response.data.data || response.data);
      setBookingSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Bokningen misslyckades. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  // Formatera datum
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('sv-SE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Bekräftelsesida
  if (bookingSuccess && bookingResult) {
    return (
      <div className="standalone-booking">
        <div className="booking-success">
          <div className="success-icon">✓</div>
          <h1>Tack för din bokning!</h1>
          <p>Bekräftelse har skickats till {participants[0].email}</p>

          <div className="booking-summary">
            <h3>{selectedCourse.courseName}</h3>
            <p>{formatDate(selectedCourse.startDate)}</p>
            <p>{selectedCourse.location}, {selectedCourse.city}</p>
            <p>{participants.length} deltagare</p>

            {bookingResult.waitlistCount > 0 && (
              <div className="waitlist-notice">
                <strong>Obs!</strong> {bookingResult.waitlistCount} deltagare är placerade på väntelista.
              </div>
            )}
          </div>

          <button
            className="btn-new-booking"
            onClick={() => window.location.reload()}
          >
            Gör en ny bokning
          </button>
        </div>
      </div>
    );
  }

  // Kurslista
  if (!selectedCourse) {
    return (
      <div className="standalone-booking">
        <header className="booking-header">
          <h1>Boka kurs</h1>
          <p>Välj den kurs du vill anmäla dig till</p>
        </header>

        {loading && <div className="loading">Laddar kurser...</div>}
        {error && <div className="error-message">{error}</div>}

        <div className="course-grid">
          {courses.map(course => (
            <div key={course.instanceId} className="course-card">
              <div className="course-card-header">
                <h2>{course.courseName}</h2>
                {course.isEarlyBird && (
                  <span className="early-bird-badge">Early Bird!</span>
                )}
              </div>

              <div className="course-card-body">
                <p className="course-date">{formatDate(course.startDate)}</p>
                <p className="course-location">{course.location}, {course.city}</p>
                <p className="course-time">{course.startTime} - {course.endTime}</p>

                <div className="course-price">
                  {course.isEarlyBird && course.originalPrice ? (
                    <>
                      <span className="price-current">{course.price} kr</span>
                      <span className="price-original">{course.originalPrice} kr</span>
                    </>
                  ) : (
                    <span className="price-current">{course.price} kr</span>
                  )}
                </div>

                <div className={`availability availability-${course.isFull ? 'red' : 'green'}`}>
                  {course.isFull ? 'Fullbokad - väntelista tillgänglig' : 'Platser tillgängliga'}
                </div>
              </div>

              <button
                className="btn-select-course"
                onClick={() => setSelectedCourse(course)}
              >
                {course.isFull ? 'Ställ dig i kö' : 'Välj kurs'}
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Bokningsformulär
  return (
    <div className="standalone-booking">
      <button className="btn-back" onClick={() => setSelectedCourse(null)}>
        ← Tillbaka till kurser
      </button>

      <div className="booking-container">
        <div className="selected-course-info">
          <h2>{selectedCourse.courseName}</h2>
          <p>{formatDate(selectedCourse.startDate)}</p>
          <p>{selectedCourse.location}, {selectedCourse.city}</p>
          <p><strong>{selectedCourse.price} kr</strong> per deltagare</p>

          {selectedCourse.isFull && (
            <div className="waitlist-warning">
              Kursen är fullbokad. Du placeras på väntelista.
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="booking-form">
          <h3>Deltagare</h3>

          <div className="info-note">
            Ange endast namn och e-post nu. Övrig information samlas in via bekräftelsemejlet.
          </div>

          {participants.map((participant, index) => (
            <div key={index} className="participant-section">
              <div className="participant-header">
                <h4>Deltagare {index + 1} {index === 0 && '(Kontaktperson)'}</h4>
                {index > 0 && (
                  <button
                    type="button"
                    className="btn-remove"
                    onClick={() => removeParticipant(index)}
                  >
                    Ta bort
                  </button>
                )}
              </div>

              <div className="form-row">
                <input
                  type="text"
                  placeholder="Förnamn *"
                  value={participant.firstName}
                  onChange={(e) => updateParticipant(index, 'firstName', e.target.value)}
                  required
                />
                <input
                  type="text"
                  placeholder="Efternamn *"
                  value={participant.lastName}
                  onChange={(e) => updateParticipant(index, 'lastName', e.target.value)}
                  required
                />
              </div>

              <input
                type="email"
                placeholder="E-post *"
                value={participant.email}
                onChange={(e) => updateParticipant(index, 'email', e.target.value)}
                required
              />
            </div>
          ))}

          <button
            type="button"
            className="btn-add-participant"
            onClick={addParticipant}
            disabled={participants.length >= 10}
          >
            + Lägg till deltagare
          </button>

          <h3>Rabattkod</h3>

          <div className="discount-section">
            <input
              type="text"
              placeholder="Rabattkod (valfritt)"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
              maxLength={9}
            />
            <button
              type="button"
              className="btn-validate"
              onClick={validateDiscount}
            >
              Använd
            </button>
          </div>

          {discountError && <div className="discount-error">{discountError}</div>}
          {validatedDiscount && (
            <div className="discount-success">
              Rabatt tillämpad: {validatedDiscount.discountType === 'percent'
                ? `${validatedDiscount.discountValue}%`
                : `${validatedDiscount.discountValue} kr`}
            </div>
          )}

          <div className="price-summary">
            <h3>Sammanfattning</h3>
            <div className="summary-row">
              <span>{participants.length} deltagare × {selectedCourse.price} kr</span>
              <span>{selectedCourse.price * participants.length} kr</span>
            </div>
            {validatedDiscount && (
              <div className="summary-row discount">
                <span>Rabatt</span>
                <span>
                  -{validatedDiscount.discountType === 'percent'
                    ? `${validatedDiscount.discountValue}%`
                    : `${validatedDiscount.discountValue * participants.length} kr`}
                </span>
              </div>
            )}
            <div className="summary-row total">
              <span>Totalt (exkl. moms)</span>
              <span>{calculatePrice()} kr</span>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            className="btn-submit"
            disabled={loading}
          >
            {loading ? 'Bokar...' : 'Slutför bokning'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default StandaloneBooking;
